import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Check, Loader2, Sparkles, ArrowRight, Gift, Ticket, ShoppingBag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import PageHero from "@/components/PageHero";
import { toast } from "sonner";
import { money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";
import { GiftCardPurchase } from "@/components/GiftCardPurchase";

function GiftFields({ g, setG }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 mt-3 p-4 rounded-xl bg-[#D98E73]/[0.07] border border-[#D98E73]/30">
      <input placeholder="Recipient's email" value={g.recipient_email} onChange={(e) => setG({ ...g, recipient_email: e.target.value })} data-testid="gift-recipient-email" className="eyw-input" />
      <input placeholder="Recipient's name (optional)" value={g.recipient_name} onChange={(e) => setG({ ...g, recipient_name: e.target.value })} data-testid="gift-recipient-name" className="eyw-input" />
      <input placeholder="A short message (optional)" value={g.gift_message} onChange={(e) => setG({ ...g, gift_message: e.target.value })} data-testid="gift-message" className="eyw-input sm:col-span-2" />
    </div>
  );
}

export default function Packages() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [gift, setGift] = useState(false);
  const [g, setG] = useState({ recipient_email: "", recipient_name: "", gift_message: "" });
  const [cert, setCert] = useState({ amount: 100, recipient_email: "", recipient_name: "", message: "" });
  const { data: giftOpts } = useQuery({ queryKey: ["gift-card-options"], queryFn: () => api.get("/gift-cards/options").then((r) => r.data) });
  const [redeemCode, setRedeemCode] = useState("");
  const { addPackage: addToCart } = useCart();

  const { data: packages = [], isLoading } = useQuery({
    queryKey: ["packages-catalog"],
    queryFn: () => api.get("/packages/catalog").then((r) => r.data),
  });

  const requireLogin = () => { toast.info("Please log in or join to continue."); window.location.href = "/login"; };

  const buy = async (p) => {
    if (!user) return requireLogin();
    if (gift && !g.recipient_email) { toast.error("Add the recipient's email to send a gift."); return; }
    setBusy(p.id);
    try {
      const { data } = await api.post(`/packages/${p.id}/purchase`, { origin_url: window.location.origin, is_gift: gift, ...g });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const buyCert = async () => {
    if (!user) return requireLogin();
    if (!cert.recipient_email) { toast.error("Add the recipient's email."); return; }
    setBusy("cert");
    try {
      const { data } = await api.post("/gift-certificates/purchase", { ...cert, amount: Number(cert.amount), origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const redeem = async () => {
    if (!user) return requireLogin();
    if (!redeemCode.trim()) { toast.error("Enter your code."); return; }
    setBusy("redeem");
    try {
      const { data } = await api.post("/gift-certificates/redeem", { code: redeemCode });
      toast.success(`${money(data.amount)} added to your account credit!`);
      setRedeemCode("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="packages-page">
      <Seo
        title="Wellness Packages & Session Bundles"
        description="Purchase multi-session wellness packages — massage, energy work, breathwork, and more. One-time purchase, no monthly fees. Gift options available."
        path="/packages"
      />
      <PageHero label="Wellness Packages" title="Choose your pathway of care"
        subtitle="One-time packages — no monthly fees. Pick your sessions from our curated offerings and use them whenever suits you." testid="packages-hero" />

      <section className="max-w-6xl mx-auto px-6 py-16">
        <label className="flex items-center gap-3 mb-6 cursor-pointer" data-testid="gift-toggle">
          <input type="checkbox" checked={gift} onChange={(e) => setGift(e.target.checked)} className="accent-[#D98E73] h-4 w-4" data-testid="gift-toggle-input" />
          <span className="text-sm text-[#1A242B] flex items-center gap-1.5"><Gift size={16} className="text-[#D98E73]" /> Buy as a gift for someone else</span>
        </label>
        {gift && <GiftFields g={g} setG={setG} />}

        {isLoading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-[#144F75]" size={28} /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {packages.map((p) => (
              <div key={p.id} className="eyw-card p-8 flex flex-col" data-testid={`package-card-${p.id}`}>
                <h3 className="font-serif text-2xl text-[#1A242B]">{p.name}</h3>
                {p.owner_type === "enhancer" && (
                  <p className="text-xs text-[#5C8A72] mt-1" data-testid={`package-owner-${p.id}`}>
                    by <Link to={`/enhancers/${p.owner_id}`} className="hover:underline">{p.owner_name || "Enhancer"}</Link>
                    {p.centers?.length > 0 && <> · at {p.centers.map((c, i) => <span key={c.id}>{i > 0 && ", "}<Link to={`/locations/${c.slug}`} className="hover:underline">{c.name}</Link></span>)}</>}
                  </p>
                )}
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-4xl font-serif text-[#144F75]">{money(Number(p.price))}</span>
                  <span className="text-sm text-[#8FA89B]">· {p.sessions} sessions</span>
                </div>
                <ListingDescription text={p.description} item={p} kind="package" className="text-sm text-[#4A5D6B] mt-4 leading-relaxed flex-1" testid={`package-desc-${p.id}`} />
                {p.eligible_services?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs font-medium text-[#1A242B] mb-2 flex items-center gap-1"><Sparkles size={13} className="text-[#D98E73]" /> Choose from</p>
                    <ul className="space-y-1.5">
                      {p.eligible_services.slice(0, 8).map((s) => (
                        <li key={s.id} className="text-sm text-[#4A5D6B] flex items-start gap-2"><Check size={14} className="text-[#5C8A72] mt-0.5 shrink-0" /> {s.title}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button onClick={() => buy(p)} disabled={busy === p.id} data-testid={`package-buy-${p.id}`} className="eyw-btn w-full mt-6 disabled:opacity-60">
                  {busy === p.id ? <Loader2 className="animate-spin" size={18} /> : gift ? <Gift size={16} /> : null} {gift ? "Gift this package" : "Purchase package"}
                </button>
                {!gift && (
                  <button onClick={() => { addToCart(p); toast.success("Added to cart"); }} data-testid={`package-addcart-${p.id}`} className="eyw-btn-ghost w-full mt-2 text-sm">
                    <ShoppingBag size={15} /> Add to cart
                  </button>
                )}
                {!gift && Number(p.price) > 0 && Number(p.price) < 20 && (
                  <p className="mt-2 text-[11px] text-[#8A4B2E] bg-[#FBEEE8] border border-[#E7C3B3] rounded-md px-2 py-1" data-testid={`min-cart-note-pkg-${p.id}`}>Must be part of a $20+ cart</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Gift certificate + redeem */}
        <div className="grid md:grid-cols-2 gap-6 mt-14">
          <GiftCardPurchase />

          <div className="eyw-card p-8" data-testid="redeem-card">
            <h3 className="font-serif text-2xl text-[#1A242B]">Redeem a gift card</h3>
            <p className="text-sm text-[#4A5D6B] mt-2 mb-4">Enter your code to add its balance to your account credit — it applies automatically at checkout.</p>
            <input placeholder="EYW-XXXX-XXXX-XXXX" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} data-testid="redeem-code" className="eyw-input w-full uppercase" />
            <button onClick={redeem} disabled={busy === "redeem"} data-testid="redeem-btn" className="eyw-btn w-full mt-4 disabled:opacity-60">
              {busy === "redeem" ? <Loader2 className="animate-spin" size={18} /> : <Ticket size={16} />} Redeem
            </button>
          </div>
        </div>

        <div className="text-center mt-12">
          <Link to="/wellness" className="eyw-btn-ghost">Browse individual offerings <ArrowRight size={16} /></Link>
        </div>
        <p className="text-center text-xs text-[#8FA89B] mt-8 max-w-xl mx-auto">
          After purchase, your sessions appear in your dashboard as credits — schedule any offering included in your package whenever you're ready.
        </p>
      </section>
    </div>
  );
}
